import { useEffect, useState } from "react"
import { useTouchUiEnabled } from "@/v2-ui/utils/utils.touchUi"

const MOBILE_BREAKPOINT = 768

/** Short-edge floor for "tablet-class". Above an iPhone landscape height (320-440),
 * below an iPad in Split View (507). */
const TABLET_MIN_EDGE_PX = 500

/**
 * "Mobile" layout: bottom-anchored popovers, full-width popover content, the
 * sidebar as a sheet, stacked filter toolbars.
 *
 * With a mouse this is exactly the width rule it has always been: a window
 * under 768 px. Nothing about a Mac or a PC changes here (FUS-1196).
 *
 * TOUCH-ONLY (FUS-1196). See the inventory in utils.touch.constants.ts.
 * On a touch device the width rule is replaced by the short edge. Width alone
 * put an iPad mini in portrait (744 px) on the phone layout and an iPhone in
 * landscape (874 px) on the desktop one, so a phone's menus changed shape as
 * it was turned, and the mini's top-bar popovers opened at the bottom of a
 * 1133 px screen. Now every iPad is a tablet whichever way it is held, and a
 * phone is a phone in both orientations.
 */
export function useIsMobile() {
  const [isNarrow, setIsNarrow] = useState<boolean | undefined>()
  const isCoarsePointer = useIsCoarsePointer()
  const hasTabletViewport = useHasTabletViewport()

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`)
    const onChange = () => {
      setIsNarrow(window.innerWidth < MOBILE_BREAKPOINT)
    }
    mql.addEventListener("change", onChange)
    setIsNarrow(window.innerWidth < MOBILE_BREAKPOINT)
    return () => mql.removeEventListener("change", onChange)
  }, [])

  if (isCoarsePointer) return !hasTabletViewport
  return !!isNarrow
}

function useMediaQuery(query: string) {
  const [matches, setMatches] = useState<boolean | undefined>()

  useEffect(() => {
    const mql = window.matchMedia(query)
    const onChange = () => setMatches(mql.matches)
    mql.addEventListener("change", onChange)
    setMatches(mql.matches)
    return () => mql.removeEventListener("change", onChange)
  }, [query])

  return !!matches
}

/**
 * True when the primary pointer is a finger or stylus rather than a mouse AND
 * the `FF_TouchUi_Root` feature flag allows the touch UI (`<html data-touch-ui>`, see
 * `utils.touchUi.ts`). Flag off = always false, i.e. production before
 * FUS-1196. Prefer this over `useIsMobile` for anything about *reach* - hit areas, the
 * room a control needs - and keep `useIsMobile` for questions that really are
 * about viewport width. See FUS-1196.
 */
export function useIsCoarsePointer() {
  const isCoarse = useMediaQuery("(pointer: coarse)")
  const isTouchUiEnabled = useTouchUiEnabled()
  return isCoarse && isTouchUiEnabled
}

/**
 * True when BOTH edges of the viewport are at least `TABLET_MIN_EDGE_PX`, i.e.
 * the short edge is tablet-sized whichever way the device is held.
 *
 * `useIsMobile` asks only about width, which cannot separate a phone from a
 * tablet: an iPhone in landscape is 874 px wide, wider than an iPad mini in
 * portrait at 744 px. The short edge can. iPad mini portrait (744) and an iPad
 * in Split View (507) pass; a phone fails in both orientations.
 *
 * `useIsMobile` uses it on touch devices, and `MapWidgetToolbarRight` picks
 * its height floor from it (FUS-1196).
 */
export function useHasTabletViewport() {
  return useMediaQuery(
    `(min-width: ${TABLET_MIN_EDGE_PX}px) and (min-height: ${TABLET_MIN_EDGE_PX}px)`,
  )
}
